import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Heart, HandHeart, Briefcase, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useFavorites } from "@/hooks/useFavorites";
import { buildPostAuthPath } from "@/lib/authRedirect";
import ShareStoryDialog from "@/components/support/ShareStoryDialog";
import FollowAthleteDialog from "@/components/support/FollowAthleteDialog";
import { PARTNERSHIP_EXPAND_EVENT } from "@/components/support/WorkWithAthleteSection";
import { trackEvent } from "@/lib/analytics";
import { sendEvent } from "@/lib/firstPartyAnalytics";
import { resolveSupportExperience, shouldTrackCommerceEvents, supportEntryFor } from "@/lib/commerce";
import BackHerGameChooser, { useShopMode } from "@/components/support/BackHerGameChooser";


export type SupportPathway = {
  key: "direct" | "cause" | "partnership";
  label: string;
  description: string;
  targetId: string;
  icon: typeof Heart;
  primary?: boolean;
};

type Props = {
  athleteId: string;
  athleteName: string;
  firstName: string;
  /** Canonical profile slug — used to build share links. */
  slug: string;
  /** Approved plain-text tagline, used for share/preview copy. */
  tagline?: string | null;
  /** Biography source; only ever used as a sanitized plain-text excerpt. */
  bio?: string | null;
  imageUrl?: string | null;
  /** Direct support is ready: enabled, approved and a valid hosted payment URL exists. */
  directSupport?: boolean;
  /** At least one published cause with a valid destination is rendered on this profile. */
  cause?: boolean;
  /** The partnership inquiry module is rendered on this profile. */
  partnership?: boolean;
  /** Verified-admin preview override (never persisted, never public). */
  commerceOverride?: boolean;
  athletePublished?: boolean;
  /** Verified-admin Admin Preview is active. */
  adminPreview?: boolean;
  /**
   * Business Hub layout only. "follow" renders just Follow + Share;
   * "back" renders just the Back Her Game pathways (and nothing when none are
   * available). Default "all" is the classic combined block, unchanged.
   */
  mode?: "all" | "follow" | "back";
  /** Reports whether any Back Her Game option is actually available. */
  onAvailability?: (available: boolean) => void;
};


const scrollToModule = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.setAttribute("tabindex", "-1");
  (el as HTMLElement).focus({ preventScroll: true });
};

/**
 * The single organising surface for every support action available on a profile.
 * Join and Share are always present; the three conditional pathways only appear
 * when the underlying module is genuinely ready, and each one routes to that
 * existing module rather than duplicating it.
 */
const AthleteSupportHub = ({
  athleteId,
  athleteName,
  firstName,
  slug,
  tagline,
  bio,
  imageUrl,
  directSupport = false,
  cause = false,
  partnership = false,
  commerceOverride = false,
  athletePublished = false,
  adminPreview = false,
  mode = "all",
  onAvailability,
}: Props) => {

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites, toggle, loading: favLoading, refresh } = useFavorites("athlete");
  const [busy, setBusy] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);
  const followBtnRef = useRef<HTMLButtonElement>(null);
  const isFollowing = favorites.has(athleteId);
  const shopMode = useShopMode(athleteId, commerceOverride, athletePublished);
  // Direct Support always comes from the real configuration; preview only simulates Shop.
  const experience = resolveSupportExperience(directSupport, shopMode !== "off");
  const entry = supportEntryFor(experience);
  const commercePreview = entry === "chooser" || entry === "shop";

  const join = async () => {
    sendEvent("follow_click", { athleteId, metadata: { following_already: isFollowing } });
    if (!isFollowing) {
      setFollowOpen(true);
      return;
    }
    if (!user) return;
    setBusy(true);
    const nowFav = await toggle(athleteId);
    setBusy(false);
    trackEvent("follow_click", { athlete: slug, following: nowFav });
    if (!nowFav) sendEvent("unfollow", { athleteId });
    toast.success(
      nowFav
        ? `You're in ${firstName}'s corner.`
        : `Removed from your athletes.`,
    );
  };




  const pathways: SupportPathway[] = [
    entry === "direct" && {
      key: "direct",
      label: `Back ${firstName} Directly`,
      description: `Send one-time or recurring support directly to ${firstName}. Secure payment is handled by her payment provider.`,
      targetId: "support",
      icon: Heart,
      primary: true,
    },
    cause && {
      key: "cause",
      label: "Support Her Cause",
      description: `Support an organization ${firstName} cares about. Contributions go to the organization, not the athlete.`,
      targetId: "causes",
      icon: HandHeart,
    },
    partnership && {
      key: "partnership",
      label: "Work With Her",
      description: `Invite ${firstName} to collaborate, speak, appear or represent your brand.`,
      targetId: "work-with",
      icon: Briefcase,
    },
  ].filter(Boolean) as SupportPathway[];

  const backCount = pathways.length + (commercePreview ? 1 : 0);
  useEffect(() => { onAvailability?.(backCount > 0); }, [backCount, onAvailability]);
  const count = mode === "follow" ? 0 : pathways.length + (commercePreview ? 1 : 0);
  if (mode === "back" && count === 0) return null;
  const gridClass =
    count === 1
      ? "grid grid-cols-1"
      : count === 2
      ? "grid grid-cols-1 sm:grid-cols-2"
      : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  const sectionId = mode === "follow" ? "follow-her" : mode === "back" ? "back-her" : "support-hub";
  const eyebrow = mode === "follow" ? "FOLLOW HER" : "BACK HER GAME";
  const heading =
    mode === "follow"
      ? `Join ${firstName}'s corner.`
      : mode === "back"
      ? `Back ${firstName}'s game.`
      : `Back ${firstName} in the way that works for you.`;
  const lede =
    mode === "follow"
      ? `Follow ${firstName} to keep her in My Corner and see her latest updates.`
      : mode === "back"
      ? `Choose how you'd like to back ${firstName}.`
      : "Every follow, share and opportunity helps more people see her story and support what comes next.";

  return (
    <section
      id={sectionId}
      aria-labelledby={`${sectionId}-heading`}
      className="scroll-mt-32 rounded-2xl bg-secondary text-secondary-foreground shadow-bold p-5 md:p-6 overflow-hidden"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-on-navy">{eyebrow}</p>
      <h2
        id={`${sectionId}-heading`}
        className="font-display text-2xl md:text-3xl uppercase leading-none mt-2 text-balance break-words"
      >
        {heading}
      </h2>
      <p className="mt-2 max-w-2xl text-sm md:text-base leading-relaxed opacity-90">
        {lede}
      </p>

      {/* Always-available actions */}
      {mode !== "back" && (<>
      <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-3">
        <button
          ref={followBtnRef}
          type="button"
          onClick={join}
          disabled={busy || favLoading}
          aria-pressed={isFollowing}
          className={`inline-flex min-h-[44px] items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary disabled:opacity-60 ${
            isFollowing
              ? "bg-secondary-foreground/10 border border-secondary-foreground/25"
              : "bg-primary text-primary-foreground hover:bg-background hover:text-foreground"
          }`}
        >
          {isFollowing ? <Check className="w-4 h-4" aria-hidden /> : <Heart className="w-4 h-4" aria-hidden />}
          <span className="break-words">
            {isFollowing ? `You're in ${firstName}'s corner` : `Follow ${firstName}`}
          </span>
        </button>

        <ShareStoryDialog
          name={athleteName}
          slug={slug}
          tagline={tagline}
          bio={bio}
          imageUrl={imageUrl}
        />

        <FollowAthleteDialog
          open={followOpen}
          onOpenChange={(o) => {
            setFollowOpen(o);
            // Return keyboard focus to the Follow button when the dialog closes.
            if (!o) window.setTimeout(() => followBtnRef.current?.focus(), 0);
          }}
          athleteId={athleteId}
          athleteName={athleteName}
          athleteSlug={slug}
          firstName={firstName}
          isFollowing={isFollowing}
          onSaved={refresh}
        />

      </div>

      <p className="mt-3 text-xs opacity-70 max-w-2xl">
        {user
          ? `Following keeps ${firstName} in My Corner. Updates appear in My Corner.`
          : `Follow with your email—no password required. Updates appear in My Corner.`}
      </p>
      </>)}


      {/* Conditional pathways — each links to the existing module below */}
      {count > 0 && (
        <div className={`mt-4 gap-3 ${gridClass}`}>
          {commercePreview && (
            <BackHerGameChooser
              firstName={firstName}
              athleteId={athleteId}
              slug={slug}
              experience={experience === "both" ? "both" : "shop_only"}
              preview={shopMode === "preview"}
              trackEvents={shouldTrackCommerceEvents({ adminPreview, shopMode })}
            />
          )}
          {pathways.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  if (p.key === "partnership") {
                    window.dispatchEvent(new CustomEvent(PARTNERSHIP_EXPAND_EVENT));
                  }
                  scrollToModule(p.targetId);
                }}
                className={`group text-left min-h-[44px] rounded-xl p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary min-w-0 ${
                  p.primary
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "bg-secondary-foreground/10 border border-secondary-foreground/20 hover:bg-secondary-foreground/15"
                }`}
              >
                <span className="flex flex-wrap items-center gap-2 min-w-0 font-display text-lg uppercase tracking-wide break-words">
                  <Icon className="w-5 h-5 shrink-0" aria-hidden />
                  {p.label}
                  <ArrowRight className="w-4 h-4 shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform" aria-hidden />
                </span>
                <span className={`mt-2 block text-sm leading-relaxed break-words ${p.primary ? "" : "opacity-90"}`}>{p.description}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default AthleteSupportHub;
